import type { EntityType, EventOption } from "../types";
import { DEFAULT_EVENT_OPTIONS, COMMON_EVENT_OPTIONS } from "../config/defaultEventOptions";
import { featuresForType, type EntityFeature } from "../config/entityFeatures";
import { hasActivityFlow, remapOptionIds, removeOptionFromFlow } from "./activityFlow";

/**
 * Turning a place's features on and off, in terms of the steps it logs.
 *
 * Features are matched to an entity's steps by name, not by id: every entity
 * carried over from the old database has its own copy of the steps with its own
 * ids, and the name is what says which step it is. That also means none of this
 * needs a migration — an entity that has "At Lockers" simply reads as having
 * lockers, today, with nothing written anywhere.
 *
 * What toggling writes depends on whether the entity has been wired up by hand:
 *
 *  - **No flow of its own** (which is all of the carried-over entities): the
 *    steps are added or removed plain, and `adoptFlow` re-derives the wiring
 *    from the type on every read. Nothing else to do.
 *  - **Hand-wired**: the step arrives carrying the type's wiring for it,
 *    translated to this entity's ids, and is spliced into the `next` of the
 *    steps the type default has pointing at it. The rest of the entity's own
 *    wiring is left exactly as it was.
 */

const typeDefaults = (type: EntityType | undefined): EventOption[] =>
  (type && DEFAULT_EVENT_OPTIONS[type]) || COMMON_EVENT_OPTIONS;

const key = (name: string) => name.trim().toLowerCase();

/** Names of the steps a feature contributes, read off the type's defaults. */
function featureStepNames(feature: EntityFeature, defaults: EventOption[]): string[] {
  return feature.steps
    .map((id) => defaults.find((d) => d.id === id)?.displayName)
    .filter((n): n is string => !!n);
}

/**
 * Which features this entity has. An entity with no steps of its own follows
 * the type default, which is the core plus every feature.
 */
export function detectFeatures(
  options: EventOption[],
  type: EntityType | undefined,
): string[] {
  const features = featuresForType(type);
  if (options.length === 0) return features.map((f) => f.id);
  const defaults = typeDefaults(type);
  const present = new Set(options.map((o) => key(o.displayName)));
  return features
    .filter((f) => featureStepNames(f, defaults).some((n) => present.has(key(n))))
    .map((f) => f.id);
}

/** The steps that belong to no feature — the core, plus anything hand-added. */
export function coreStepNames(type: EntityType | undefined): string[] {
  const defaults = typeDefaults(type);
  const claimed = new Set(
    featuresForType(type).flatMap((f) => featureStepNames(f, defaults).map(key)),
  );
  return defaults.filter((d) => !claimed.has(key(d.displayName))).map((d) => d.displayName);
}

/**
 * Turn a feature on or off, returning the entity's new step list.
 *
 * Turning one off on an entity that had no steps of its own has to materialise
 * the type default first — it can't follow the type any more once it differs
 * from it.
 */
export function toggleFeature(
  options: EventOption[],
  type: EntityType | undefined,
  featureId: string,
  on: boolean,
  newId: () => string,
): EventOption[] {
  const feature = featuresForType(type).find((f) => f.id === featureId);
  if (!feature) return options;
  const defaults = typeDefaults(type);
  const current = options.length > 0 ? options : remapOptionIds(defaults, newId);

  if (!on) {
    const names = new Set(featureStepNames(feature, defaults).map(key));
    return current
      .filter((o) => names.has(key(o.displayName)))
      .reduce((acc, o) => removeOptionFromFlow(acc, o.id), current);
  }

  return feature.steps.reduce((acc, stepId) => addStep(acc, defaults, stepId, newId), current);
}

/** Put one of the type's steps back onto an entity that dropped it. */
function addStep(
  options: EventOption[],
  defaults: EventOption[],
  stepId: string,
  newId: () => string,
): EventOption[] {
  const source = defaults.find((d) => d.id === stepId);
  if (!source) return options;
  if (options.some((o) => key(o.displayName) === key(source.displayName))) return options;

  const wired = hasActivityFlow(options);
  const added: EventOption = { ...source, id: newId() };
  // An entity with no wiring of its own keeps borrowing the type's, so the step
  // arrives bare rather than carrying edges nothing else here has.
  if (!wired) {
    delete added.start;
    delete added.next;
    delete added.requires;
    delete added.once;
  }

  // Line the entity's steps up against the type's, so the new one can be put
  // where the type keeps it and pointed at by the same steps.
  const defaultIndexOf = (o: EventOption) =>
    defaults.findIndex((d) => key(d.displayName) === key(o.displayName));
  const sourceIndex = defaults.indexOf(source);

  let next: EventOption[];
  if (wired) {
    const entityIdByName = new Map(options.map((o) => [key(o.displayName), o.id] as const));
    entityIdByName.set(key(source.displayName), added.id);
    const translate = (ids: string[] | undefined) =>
      ids
        ?.map((id) => defaults.find((d) => d.id === id)?.displayName)
        .map((name) => (name ? entityIdByName.get(key(name)) : undefined))
        .filter((id): id is string => !!id);
    added.next = translate(source.next) ?? [];
    const requires = translate(source.requires);
    if (requires && requires.length > 0) added.requires = requires;
    else delete added.requires;

    // A step whose successors someone has cut down by hand is left cut down:
    // saying a ride has a preshow shouldn't quietly undo the narrowing that
    // said you can only leave its queue one way. If that leaves the new step
    // unreachable, `validateActivityFlow` says so rather than this guessing.
    const narrowed = (o: EventOption, d: EventOption | undefined) =>
      (d?.next ?? []).some((id) => {
        const name = defaults.find((x) => x.id === id)?.displayName;
        const own = name ? entityIdByName.get(key(name)) : undefined;
        return !!own && own !== added.id && !o.next?.includes(own);
      });

    next = options.map((o) => {
      const d = defaults[defaultIndexOf(o)];
      if (!d?.next?.includes(stepId) || o.next?.includes(added.id)) return o;
      if (narrowed(o, d)) return o;
      // Canonical position: after however many of the type's own successors
      // this step already lists.
      const before = d.next
        .slice(0, d.next.indexOf(stepId))
        .filter((id) => {
          const name = defaults.find((x) => x.id === id)?.displayName;
          return name ? o.next?.includes(entityIdByName.get(key(name)) ?? "") : false;
        }).length;
      const merged = [...(o.next ?? [])];
      merged.splice(before, 0, added.id);
      return { ...o, next: merged };
    });
  } else {
    next = [...options];
  }

  const insertAt = next.reduce(
    (at, o, i) => (defaultIndexOf(o) >= 0 && defaultIndexOf(o) < sourceIndex ? i + 1 : at),
    0,
  );
  next.splice(insertAt, 0, added);
  return next;
}
